import React, { useState, useCallback, useEffect } from "react";
import { ConversationSidebar, Conversation } from "./ConversationSidebar";
import { ChatView, ChatMessage } from "./ChatView";
import { SpectraLogo } from "./SpectraLogo";
import { Lang, translations } from "./i18n";
import { ThemeProvider, useTheme, useColors } from "./theme";

const STORAGE_KEY = "hairgpt_conversations";
const SESSION_KEY = "hairgpt_unlocked";
const LANG_KEY = "hairgpt_lang";
const ACCESS_CODE = "1212";

function loadConversations(): Conversation[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveConversations(convs: Conversation[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(convs));
}

function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

function generateTitle(firstMessage: string): string {
  const words = firstMessage.split(/\s+/).slice(0, 6);
  const title = words.join(" ");
  return title.length > 40 ? title.slice(0, 40) + "…" : title;
}

const LanguageToggle: React.FC<{ lang: Lang; onToggle: () => void }> = ({ lang, onToggle }) => {
  const c = useColors();
  return (
    <button
      onClick={onToggle}
      className="flex items-center h-9 rounded-full overflow-hidden text-[11px] font-light tracking-wide transition-all duration-300"
      style={{ border: `1px solid ${c.langToggle.border}`, background: c.langToggle.bg }}
    >
      <span className={`px-2.5 py-1 transition-all duration-300`} style={{ color: lang === "en" ? "#EAB776" : c.langToggle.inactive }}>
        EN
      </span>
      <span className="w-px h-3" style={{ background: c.langToggle.divider }} />
      <span className={`px-2.5 py-1 transition-all duration-300`} style={{ color: lang === "he" ? "#EAB776" : c.langToggle.inactive }}>
        HE
      </span>
    </button>
  );
};

const ThemeToggle: React.FC = () => {
  const { isDark, toggleTheme } = useTheme();
  const c = useColors();
  return (
    <button
      onClick={toggleTheme}
      className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300"
      style={{ border: `1px solid ${c.langToggle.border}`, background: c.langToggle.bg }}
      title={isDark ? "Light mode" : "Dark mode"}
    >
      {isDark ? (
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} style={{ color: "#EAB776" }}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
        </svg>
      ) : (
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} style={{ color: "#EAB776" }}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
        </svg>
      )}
    </button>
  );
};

const HairGPTInner: React.FC = () => {
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem(SESSION_KEY) === ACCESS_CODE);
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState("");
  const [lang, setLang] = useState<Lang>(() => (localStorage.getItem(LANG_KEY) as Lang) || "en");

  const [conversations, setConversations] = useState<Conversation[]>(loadConversations);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const { isDark } = useTheme();
  const c = useColors();
  const t = translations[lang];

  const toggleLang = useCallback(() => {
    setLang((prev) => {
      const next = prev === "en" ? "he" : "en";
      localStorage.setItem(LANG_KEY, next);
      return next;
    });
  }, []);

  useEffect(() => {
    saveConversations(conversations);
  }, [conversations]);

  const activeConv = conversations.find((c) => c.id === activeId) || null;
  const activeMessages: ChatMessage[] = activeConv?.messages || [];

  // Browser back button: entering a chat pushes history, back returns to landing
  useEffect(() => {
    const onPopState = (e: PopStateEvent) => {
      if (e.state?.chatId) {
        setActiveId(e.state.chatId);
      } else {
        setActiveId(null);
      }
      setSidebarOpen(false);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const navigateToChat = useCallback((id: string) => {
    window.history.pushState({ chatId: id }, "", `/hairgpt?c=${id}`);
  }, []);

  const navigateToLanding = useCallback(() => {
    window.history.pushState(null, "", "/hairgpt");
  }, []);

  const handleNew = useCallback(() => {
    setActiveId(null);
    setSidebarOpen(false);
    navigateToLanding();
  }, [navigateToLanding]);

  const handleSelect = useCallback((id: string) => {
    setActiveId(id);
    setSidebarOpen(false);
    navigateToChat(id);
  }, [navigateToChat]);

  const handleDelete = useCallback((id: string) => {
    setConversations((prev) => prev.filter((c) => c.id !== id));
    if (activeId === id) {
      setActiveId(null);
      navigateToLanding();
    }
  }, [activeId, navigateToLanding]);

  const handleSend = useCallback(async (text: string) => {
    const userMsg: ChatMessage = { role: "user", content: text, timestamp: new Date().toISOString() };

    let convId = activeId;
    let updatedMessages: ChatMessage[];

    if (!convId) {
      convId = generateId();
      updatedMessages = [userMsg];
      const newConv: Conversation = {
        id: convId,
        title: generateTitle(text),
        createdAt: new Date().toISOString(),
        messages: updatedMessages,
      };
      setConversations((prev) => [newConv, ...prev]);
      setActiveId(convId);
      navigateToChat(convId);
    } else {
      updatedMessages = [...activeMessages, userMsg];
      setConversations((prev) =>
        prev.map((c) => (c.id === convId ? { ...c, messages: updatedMessages } : c))
      );
    }

    setLoading(true);

    try {
      const apiMessages = updatedMessages.map((m) => ({
        role: m.role,
        content: m.content,
      }));

      const res = await fetch("/.netlify/functions/hairgpt", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Access-Code": ACCESS_CODE,
        },
        body: JSON.stringify({ messages: apiMessages }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${res.status}`);
      }

      const data = await res.json();

      const assistantMsg: ChatMessage = {
        role: "assistant",
        content: data.answer || "Could not process the question.",
        bullets: data.bullets || [],
        chart: data.chart || null,
        confidence: data.confidence || "medium",
        suggestedFollowUps: data.suggestedFollowUps || [],
        timestamp: new Date().toISOString(),
      };

      const finalId = convId;
      setConversations((prev) =>
        prev.map((c) =>
          c.id === finalId ? { ...c, messages: [...c.messages, assistantMsg] } : c
        )
      );
    } catch (e: any) {
      const errorMsg: ChatMessage = {
        role: "assistant",
        content: `Error: ${e.message || "Server issue"}. Please try again.`,
        timestamp: new Date().toISOString(),
      };
      const finalId = convId;
      setConversations((prev) =>
        prev.map((c) =>
          c.id === finalId ? { ...c, messages: [...c.messages, errorMsg] } : c
        )
      );
    } finally {
      setLoading(false);
    }
  }, [activeId, activeMessages, navigateToChat]);

  if (!unlocked) {
    return (
      <div className="flex items-center justify-center px-6 relative overflow-hidden" style={{ minHeight: "100dvh", background: c.bg.page }}>
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute top-[20%] left-1/2 -translate-x-1/2 w-[600px] h-[600px]"
            style={{ background: "radial-gradient(ellipse 50% 40% at 50% 40%, rgba(234,183,118,0.07) 0%, transparent 70%)" }}
          />
        </div>

        <div className="fixed z-50 flex items-center gap-2" style={{ top: 'calc(1rem + var(--safe-top))', right: 'calc(1rem + var(--safe-right))' }}>
          <ThemeToggle />
          <LanguageToggle lang={lang} onToggle={toggleLang} />
        </div>

        <div className="w-full max-w-sm text-center relative z-10">
          <div className="mx-auto mb-8">
            <SpectraLogo size={72} className="mx-auto drop-shadow-[0_0_20px_rgba(234,183,118,0.2)]" />
          </div>
          <h3 className="text-2xl font-extralight mb-1 tracking-[-0.02em]" style={{ color: c.text.primary }}>
            Hair<span className="font-light text-transparent bg-clip-text bg-gradient-to-r from-[#EAB776] to-[#B18059]">GPT</span>
          </h3>
          <p className="text-xs mb-10 font-light" style={{ color: c.text.muted }}>{t.enterCode}</p>
          <input
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={4}
            value={code}
            onChange={(e) => {
              const digits = e.currentTarget.value.replace(/\D/g, "");
              setCode(digits);
              if (codeError) setCodeError("");
              if (digits.length === 4 && digits !== ACCESS_CODE) {
                setCodeError(t.wrongCode);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && code === ACCESS_CODE) {
                sessionStorage.setItem(SESSION_KEY, ACCESS_CODE);
                setUnlocked(true);
              }
            }}
            className="w-full text-center tracking-[0.6em] text-2xl font-light rounded-2xl px-4 py-4 focus:outline-none transition-all duration-500"
            style={{
              background: c.bg.inputBg,
              color: c.text.primary,
              border: `1px solid ${c.bg.inputBorder}`,
            }}
            placeholder="• • • •"
            autoFocus
          />
          {codeError && <p className="text-xs text-red-400/70 mt-3 font-light">{codeError}</p>}
          <button
            onClick={() => {
              if (code === ACCESS_CODE) {
                sessionStorage.setItem(SESSION_KEY, ACCESS_CODE);
                setUnlocked(true);
              } else {
                setCodeError(t.wrongCode);
              }
            }}
            className="mt-8 px-10 py-3 rounded-full bg-gradient-to-r from-[#EAB776] to-[#B18059] text-sm font-medium text-white hover:shadow-[0_0_30px_rgba(234,183,118,0.3)] transition-all duration-500"
          >
            {t.enter}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex overflow-hidden relative" style={{ height: "100dvh", background: c.bg.page }}>
      <ConversationSidebar
        conversations={conversations}
        activeId={activeId}
        onSelect={handleSelect}
        onNew={handleNew}
        onDelete={handleDelete}
        open={sidebarOpen}
        onToggle={() => setSidebarOpen((v) => !v)}
        lang={lang}
      />
      <main className="flex-1 flex flex-col min-w-0 relative">
        <div className="w-full flex-1 flex flex-col min-w-0 overflow-hidden" style={{ background: c.bg.shell }}>

          {/* Top bar */}
          <div className="h-12 flex items-center justify-between px-3 sm:px-6 lg:px-10 flex-shrink-0 relative z-10" style={{ borderBottom: `1px solid ${c.bg.topBarBorder}` }}>
            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={() => setSidebarOpen((v) => !v)}
                className="w-10 h-10 rounded-lg flex items-center justify-center transition-all flex-shrink-0"
                style={{ color: c.text.icon }}
              >
                <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5" />
                </svg>
              </button>
              {activeConv ? (
                <span className="text-xs font-light truncate max-w-[140px] sm:max-w-[200px]" style={{ color: c.text.dimmed }}>
                  {activeConv.title}
                </span>
              ) : (
                <span className="text-xs font-light" style={{ color: c.text.faint }}>HairGPT</span>
              )}
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              {activeConv && (
                <button
                  onClick={handleNew}
                  className="w-10 h-10 rounded-lg flex items-center justify-center transition-all"
                  style={{ color: c.text.icon }}
                  title="New chat"
                >
                  <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                </button>
              )}
              <ThemeToggle />
              <LanguageToggle lang={lang} onToggle={toggleLang} />
              <a href="/" className="hidden sm:block transition-all duration-300 text-[11px] font-light tracking-wide" style={{ color: c.text.link }}>
                {t.backToSite}
              </a>
            </div>
          </div>
          <ChatView messages={activeMessages} onSend={handleSend} loading={loading} lang={lang} />
        </div>
      </main>
    </div>
  );
};

export const HairGPTPage: React.FC = () => (
  <ThemeProvider>
    <HairGPTInner />
  </ThemeProvider>
);
